import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery } from "@tanstack/react-query";
import * as api from "api";
import { ErrorAlert } from "component/ErrorAlert";
import { FormSelect } from "component/FormSelect";
import { DEFAULT_TIME_ZONE } from "constant";
import { AssetKindSelect, AssetSelect } from "module/common";
import { useEffect, useMemo, useState } from "react";
import { Route, Routes, matchPath, useLocation, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";
import { HospitalRouteItem, RecommendationWithRelated, User } from "type/model/api";
import { getApiErrorMessage } from "utility/error-message";
import { pathWithSearchParams } from "utility/router";
import { shiftLabel } from "utility/shift";
import { transformEnumValue, transformShiftView } from "utility/transformer";
import { DestinationOrderDialog } from "./DestinationOrderDialog";

type TAssetPanel = { isSelected?: boolean };

const RECOMMENDATION_SORT_MODES = ["cost_and_benefit", "fastest_to_slowest"] as const;

const SORT_MODE_LABELS: Record<(typeof RECOMMENDATION_SORT_MODES)[number], string> = {
  cost_and_benefit: "Cost and benefit",
  fastest_to_slowest: "Fastest to slowest",
};

const Container = styled("div")(() => ({
  width: "100%",
  height: "100%",
  display: "flex",
  padding: "32px",
  flexDirection: "column",
}));

const LoadingContainer = styled("div")(() => ({
  width: "100%",
  height: "calc(100vh - 200px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  paddingBottom: "48px",
}));

const AssetPanel = styled("div")<TAssetPanel>(({ theme, isSelected }) => ({
  width: "100%",
  border: "2px solid #D3D3D3",
  display: "flex",
  padding: "32px",
  marginTop: "16px",
  flexDirection: "column",
  borderRadius: theme.spacing(2),
  ...(isSelected && { border: "2px solid #4166e1" }),
}));

export const Recommendations = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { currentUser } = useRouteLoaderData("main") as { currentUser: User };
  const params = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<{ id: number | null; type: "recommendation" | "manual asset" }>();
  const [manualAsset, setManualAsset] = useState<number | null | undefined>();
  const isDestinationOrderDialogOpen = !!matchPath(
    "/main/dashboard/mission/:missionId/recommendations/destination-order",
    pathname
  );

  const [excludedAssetKinds, setExcludedAssetKinds] = useState<string[]>([]);
  const [sortMode, setSortMode] = useState<string>(RECOMMENDATION_SORT_MODES[0]);

  const recommendationsQuery = useQuery({
    queryKey: ["recommendations", params.missionId, excludedAssetKinds, sortMode],
    queryFn: () =>
      api.generateRecommendations(params.missionId ?? "", {
        missionId: params.missionId ?? "",
        excluded_asset_kinds: excludedAssetKinds,
        recommendation_sort_mode: sortMode,
      }),
    refetchOnWindowFocus: false,
  });

  // FOR DEMO PURPOSES ONLY - for adrian to be able to see custom columns
  // ----------------------------------------------
  const currentuserQuery = useQuery({
    queryKey: ["user"],
    queryFn: () => api.fetchCurrentUser(),
    refetchOnWindowFocus: false,
  });

  const isAdrian = "adrian.wilson@aeromedicalaustralia.com.au" === currentuserQuery.data?.data?.email;
  // ----------------------------------------------

  const recommendations = recommendationsQuery.data?.data.sort(
    (a, b) => (a.position ?? Infinity) - (b.position ?? Infinity)
  );

  const selectedRecommendation = useMemo(() => {
    if (selection?.type !== "recommendation" || !selection?.id || !recommendationsQuery.data?.data.length) {
      return null;
    }

    return recommendationsQuery.data.data.find((recommendation) => recommendation.id === selection.id) ?? null;
  }, [recommendationsQuery.data?.data, selection]);

  useEffect(() => {
    if (isDestinationOrderDialogOpen && !selectedRecommendation) {
      navigate(".");
    }
  }, [isDestinationOrderDialogOpen, selectedRecommendation, navigate]);

  const selectRecommendation = async (recommendationId?: number) => {
    setSelection({ id: recommendationId ? recommendationId : null, type: "recommendation" });
  };

  const selectManualAsset = (assetId?: number) => {
    assetId && setManualAsset(assetId);
    setSelection({ id: assetId ?? null, type: "manual asset" });
  };

  const hasMultiPatientMission = useMemo(() => {
    return recommendationsQuery.data?.data?.some((recommendation) => recommendation.mission?.is_multipatient);
  }, [recommendationsQuery.data]);

  useEffect(() => {
    setSelection((current) => (current?.type === "recommendation" ? undefined : current));
  }, [excludedAssetKinds, sortMode]);

  const handleUseSelectedRecommendation = async (
    recommendation: RecommendationWithRelated,
    modifiedHospitalRouteItems?: HospitalRouteItem[] | null
  ) => {
    const payload: any = {
      mId: params.missionId,
      recommendationId: recommendation.id!,
    };

    if (
      recommendation.mission?.is_multipatient &&
      (modifiedHospitalRouteItems ?? recommendation.hospital_route_items)
    ) {
      payload.hospital_route_items = (modifiedHospitalRouteItems ?? recommendation.hospital_route_items).map(
        (item) => ({
          patient_identifier: item.task.patient_identifier,
          action: item.action,
        })
      );
    }

    await api.selectRecommendation(payload.mId, payload.recommendationId, payload);
    navigate(`/main/dashboard/mission/edit/${params.missionId}/details`);
  };

  const handleDialogClose = () => {
    navigate(pathWithSearchParams("."));
  };

  const handleSelection = async () => {
    try {
      const mId = params.missionId ?? "";
      if (selectedRecommendation) {
        if (selectedRecommendation.mission?.is_multipatient && selectedRecommendation?.hospital_route_items?.length) {
          navigate("destination-order");
        } else {
          await handleUseSelectedRecommendation(selectedRecommendation);
        }
      }
      if (selection?.type === "manual asset" && selection?.id) {
        await api.updateMission(mId, { mission: { asset_id: selection.id } });
        navigate(`/main/dashboard/mission/edit/${params.missionId}/details`);
      }
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    }
  };

  const TimeConverter = ({ minutes }: { minutes: number }) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return (
      <div>
        {hours}hr {remainingMinutes}min
      </div>
    );
  };

  const formatSpecialCondition = (inputString: string) => {
    const words: string[] = inputString.split("_");
    const formattedWords: string[] = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
    const formattedString: string = formattedWords.join(" ");
    return formattedString;
  };

  // I can't seem to destructure these from the query
  const fromHospital = recommendations?.[0]?.mission?.tasks?.[0]?.from_hospital?.name;
  const toHospital = recommendations?.[0]?.mission?.tasks?.[0]?.to_hospital?.name;
  const specialCondition = recommendations?.[0]?.mission?.assessment_condition;

  const formatNumberWithCommas = (number?: number) => {
    if (number === undefined) {
      return null;
    }
    return number.toLocaleString();
  };

  return (
    <>
      {recommendationsQuery.isPending ? (
        <LoadingContainer>
          <CircularProgress color="secondary" />
          <Typography mt={3}>Loading recommendations</Typography>
        </LoadingContainer>
      ) : (
        <Container>
          <Typography variant="h4">Recommendations</Typography>
          <Typography variant="body1" pt={2} mb={2}>
            Recommendations based on the following factors:
          </Typography>
          {specialCondition && (
            <Typography variant="body1" pt={2} mb={2}>
              <b>Special Conditions:</b>&nbsp;
              {formatSpecialCondition(specialCondition || ("" as string))}
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Typography variant="body1" mb={2}>
              {fromHospital && <b>Referring Hospital: </b>}
              {fromHospital}&nbsp;&nbsp;
            </Typography>
            <Typography variant="body1" mb={2}>
              {toHospital && <b>Destination Hospital: </b>}
              {toHospital}
            </Typography>
          </Box>
          {!hasMultiPatientMission ? (
            <>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2, mb: 2 }}>
                <AssetKindSelect
                  label="Excluded asset types"
                  value={excludedAssetKinds}
                  onChange={setExcludedAssetKinds}
                />
                <Typography variant="body2">Order by</Typography>
                <Box sx={{ minWidth: "220px" }}>
                  <FormSelect
                    fullWidth
                    value={sortMode}
                    onChange={(event) => setSortMode(String(event.target.value))}
                    items={RECOMMENDATION_SORT_MODES.map((mode) => ({
                      value: mode,
                      element: (
                        <MenuItem key={mode} value={mode} autoFocus={false}>
                          <Typography variant="body2">{SORT_MODE_LABELS[mode]}</Typography>
                        </MenuItem>
                      ),
                      readOnlyText: SORT_MODE_LABELS[mode],
                    }))}
                  />
                </Box>
              </Box>
              {excludedAssetKinds.length > 0 && (
                <Typography variant="body1" mb={2}>
                  Excluding {excludedAssetKinds.map((kind) => transformEnumValue(kind)).join(", ")}. Fewer than three
                  recommendations can be shown when the filter leaves fewer available.
                </Typography>
              )}
            </>
          ) : null}
          {recommendations?.length === 0 ? (
            <AssetPanel>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Asset</TableCell>
                    <TableCell>Time to patient</TableCell>
                    <TableCell>Time out of hospital</TableCell>
                    <TableCell>Total mission time</TableCell>
                    {isAdrian && (
                      <TableCell>
                        <i style={{ color: "#616161" }}>Cost</i>
                      </TableCell>
                    )}
                    {isAdrian && (
                      <TableCell>
                        <i style={{ color: "#616161" }}>CO₂</i>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key="n/a" sx={{ verticalAlign: "top", "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      N/A
                    </TableCell>
                    <TableCell>N/A</TableCell>
                    <TableCell>N/A</TableCell>
                    <TableCell>N/A</TableCell>
                    {isAdrian && (
                      <TableCell>
                        <i style={{ color: "#616161" }}>N/A</i>
                      </TableCell>
                    )}
                    {isAdrian && (
                      <TableCell>
                        <i style={{ color: "#616161" }}>N/A</i>
                      </TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </AssetPanel>
          ) : (
            recommendations?.map((asset, i) => {
              const consideredShift = asset.shift
                ? shiftLabel(transformShiftView(asset.shift), currentUser.time_zone || DEFAULT_TIME_ZONE)
                : null;

              return (
                //  @ts-ignore - this also incorrectly typed the asset id needs to be made mandatory in the swagger type
                <AssetPanel
                  isSelected={asset.id === selection?.id && selection?.type === "recommendation"}
                  key={`Recommendation ${i}`}
                  onClick={() => selectRecommendation(asset.id)}
                >
                  <Typography variant="h3" mb={2}>
                    Recommendation #{i + 1}
                  </Typography>
                  {asset.warning_messages &&
                    asset.warning_messages.map((warning, i) => (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          paddingLeft: "14px",
                          alignItems: "center",
                          height: "48px",
                          width: "100%",
                          backgroundColor: "#FECACA",
                        }}
                        key={i}
                      >
                        <WarningIcon sx={{ marginRight: "10px", color: "#DC2626" }} /> {warning.message}
                      </Box>
                    ))}
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "30%" }}>Asset</TableCell>
                        {!asset.mission?.is_multipatient ? (
                          <>
                            <TableCell>Time to patient</TableCell>
                            <TableCell>Time out of hospital</TableCell>
                            <TableCell>Total medical time</TableCell>
                            <TableCell>Total mission time</TableCell>
                          </>
                        ) : (
                          <TableCell>Time to completion</TableCell>
                        )}
                        {isAdrian && (
                          <TableCell>
                            <i style={{ color: "#616161" }}>Cost</i>
                          </TableCell>
                        )}
                        {isAdrian && (
                          <TableCell>
                            <i style={{ color: "#616161" }}>CO₂</i>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow
                        key={asset.id}
                        sx={{ verticalAlign: "top", "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography>{asset?.asset?.name}</Typography>
                            <Typography>{asset?.asset?.aircraft?.call_sign}</Typography>
                            <Typography>{asset?.asset?.aircraft?.registration}</Typography>
                            <Typography>{asset?.asset?.road_ambulance?.call_sign}</Typography>
                            {consideredShift ? <Typography>{consideredShift}</Typography> : null}
                          </Box>
                        </TableCell>
                        {!asset.mission?.is_multipatient ? (
                          <>
                            <TableCell>
                              <TimeConverter minutes={asset.time_to_patient ?? 0} />
                            </TableCell>
                            <TableCell>
                              <TimeConverter minutes={asset.time_out_of_hospital ?? 0} />
                            </TableCell>
                            <TableCell>
                              <TimeConverter minutes={asset.total_medical_time ?? 0} />
                            </TableCell>
                            <TableCell>
                              <TimeConverter minutes={asset.total_mission_time ?? 0} />
                            </TableCell>
                          </>
                        ) : (
                          <TableCell>
                            <TimeConverter minutes={asset.time_to_completion ?? 0} />
                          </TableCell>
                        )}

                        {isAdrian && (
                          <TableCell>
                            <i style={{ color: "#616161" }}>${formatNumberWithCommas(asset.cost)}</i>
                          </TableCell>
                        )}
                        {isAdrian && (
                          <TableCell>
                            <i style={{ color: "#616161" }}>{Math.ceil(asset.co2_emissions ?? 0)}kg</i>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </AssetPanel>
              );
            })
          )}
          {!hasMultiPatientMission ? (
            <AssetPanel isSelected={selection?.type === "manual asset"}>
              <Typography variant="h4" mb={2}>
                Manual tasking
              </Typography>
              <AssetSelect
                label="Asset selection"
                value={manualAsset}
                onChange={(e) => {
                  const val = e.target.value as number;
                  selectManualAsset(val);
                }}
              />
            </AssetPanel>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", py: 2, gap: 2 }}>
            {errorMessage ? <ErrorAlert>{errorMessage}</ErrorAlert> : null}
            <Button
              sx={{ width: "100px", alignSelf: "flex-start" }}
              type="button"
              loading={recommendationsQuery.isPending}
              variant="contained"
              disabled={selection?.id === undefined}
              onClick={() => {
                handleSelection();
              }}
            >
              Continue
            </Button>
          </Box>
        </Container>
      )}
      {selectedRecommendation?.mission?.is_multipatient && selectedRecommendation?.hospital_route_items?.length ? (
        <Routes>
          <Route
            path="destination-order"
            element={
              <DestinationOrderDialog
                recommendation={selectedRecommendation}
                onConfirm={handleUseSelectedRecommendation}
                onClose={handleDialogClose}
              />
            }
          />
        </Routes>
      ) : null}
    </>
  );
};
